import { z } from 'zod'

// Enhanced JSON Schema types we support for conversion
type JsonSchemaPrimitiveType =
  | 'string'
  | 'number'
  | 'integer'
  | 'boolean'
  | 'object'
  | 'array'
  | 'null'

type JsonSchemaBase = {
  description?: string
  nullable?: boolean
  title?: string
  default?: any
  const?: any
  enum?: any[]
}

type JsonSchemaString = JsonSchemaBase & {
  type: 'string'
  enum?: string[]
  format?: string
  pattern?: string
  minLength?: number
  maxLength?: number
}

type JsonSchemaNumber = JsonSchemaBase & {
  type: 'number' | 'integer'
  minimum?: number
  maximum?: number
  multipleOf?: number
}

type JsonSchemaBoolean = JsonSchemaBase & {
  type: 'boolean'
}

type JsonSchemaNull = JsonSchemaBase & {
  type: 'null'
}

type JsonSchemaArray = JsonSchemaBase & {
  type: 'array'
  items?: JsonSchema | JsonSchema[]
  minItems?: number
  maxItems?: number
}

type JsonSchemaObject = JsonSchemaBase & {
  type: 'object'
  properties?: Record<string, JsonSchema>
  required?: string[]
  additionalProperties?: boolean | JsonSchema
  patternProperties?: Record<string, JsonSchema>
}

// Support for anyOf, oneOf, allOf
type JsonSchemaComposite = JsonSchemaBase & {
  anyOf?: JsonSchema[]
  oneOf?: JsonSchema[]
  allOf?: JsonSchema[]
}

type JsonSchema =
  | JsonSchemaString
  | JsonSchemaNumber
  | JsonSchemaBoolean
  | JsonSchemaNull
  | JsonSchemaArray
  | JsonSchemaObject
  | JsonSchemaComposite
  | { $ref: string } // Support for $ref (basic)
  | { type?: never; const: any } // Support for const schemas
  | { type?: never; enum: any[] } // Support for top-level enums

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isJsonSchema = (value: unknown): value is JsonSchema => {
  if (!isRecord(value)) return false
  const schema = value as Record<string, unknown>

  // Support schemas with type
  const t = schema.type
  if (
    t === 'string' ||
    t === 'number' ||
    t === 'integer' ||
    t === 'boolean' ||
    t === 'array' ||
    t === 'object' ||
    t === 'null'
  ) {
    return true
  }

  // Support schemas without type but with other schema keywords
  if (schema.anyOf || schema.oneOf || schema.allOf) {
    return true
  }

  // Support const and enum schemas
  if (schema.const !== undefined || schema.enum) {
    return true
  }

  // Support $ref schemas (basic)
  if (schema.$ref) {
    return true
  }

  return false
}

const applyNullable = (schema: z.ZodTypeAny, json: JsonSchema | undefined): z.ZodTypeAny => {
  if (json && 'nullable' in json && json.nullable) {
    return schema.nullable()
  }
  return schema
}

const convertJsonSchemaToZod = (schema: JsonSchema): z.ZodTypeAny => {
  // Handle const schemas
  if ('const' in schema && schema.const !== undefined) {
    return applyNullable(z.literal(schema.const), schema)
  }

  // Handle top-level enum schemas
  if ('enum' in schema && Array.isArray(schema.enum) && schema.enum.length > 0) {
    // For mixed-type enums, create a union of literals
    if (schema.enum.length === 1) {
      return applyNullable(z.literal(schema.enum[0]), schema)
    }
    const literals = schema.enum.map((val) => z.literal(val))
    return applyNullable(
      z.union(literals as [z.ZodLiteral<any>, z.ZodLiteral<any>, ...z.ZodLiteral<any>[]]),
      schema
    )
  }

  // Handle composite schemas (anyOf, oneOf, allOf)
  if ('anyOf' in schema && Array.isArray(schema.anyOf)) {
    const options = schema.anyOf.map(convertJsonSchemaToZod)
    if (options.length === 1) {
      return applyNullable(options[0], schema)
    }
    return applyNullable(
      z.union(options as [z.ZodTypeAny, z.ZodTypeAny, ...z.ZodTypeAny[]]),
      schema
    )
  }

  if ('oneOf' in schema && Array.isArray(schema.oneOf)) {
    const options = schema.oneOf.map(convertJsonSchemaToZod)
    if (options.length === 1) {
      return applyNullable(options[0], schema)
    }
    return applyNullable(
      z.union(options as [z.ZodTypeAny, z.ZodTypeAny, ...z.ZodTypeAny[]]),
      schema
    )
  }

  if ('allOf' in schema && Array.isArray(schema.allOf)) {
    // For allOf, we'll intersect the schemas (simplified approach)
    const schemas = schema.allOf.map(convertJsonSchemaToZod)
    if (schemas.length === 1) {
      return applyNullable(schemas[0], schema)
    }
    // For now, just return the first schema as intersection is complex
    return applyNullable(schemas[0] || z.unknown(), schema)
  }

  // Handle $ref schemas (basic support)
  if ('$ref' in schema && schema.$ref) {
    return z.unknown()
  }

  // Handle typed schemas
  if (!('type' in schema) || !schema.type) {
    return z.unknown()
  }

  switch (schema.type as JsonSchemaPrimitiveType) {
    case 'string': {
      const stringSchema = schema as JsonSchemaString
      if (Array.isArray(stringSchema.enum) && stringSchema.enum.length > 0) {
        // Ensure at least one element for z.enum typing
        const values = stringSchema.enum as [string, ...string[]]
        return applyNullable(z.enum(values), schema)
      }

      let zodString = z.string()

      // Apply string constraints
      if (stringSchema.minLength !== undefined) {
        zodString = zodString.min(stringSchema.minLength)
      }
      if (stringSchema.maxLength !== undefined) {
        zodString = zodString.max(stringSchema.maxLength)
      }
      if (stringSchema.pattern) {
        try {
          zodString = zodString.regex(new RegExp(stringSchema.pattern))
        } catch {
          // Invalid regex pattern, skip constraint
        }
      }

      return applyNullable(zodString, schema)
    }
    case 'number':
    case 'integer': {
      const numberSchema = schema as JsonSchemaNumber
      let zodNumber = schema.type === 'integer' ? z.number().int() : z.number()

      // Apply number constraints
      if (numberSchema.minimum !== undefined) {
        zodNumber = zodNumber.min(numberSchema.minimum)
      }
      if (numberSchema.maximum !== undefined) {
        zodNumber = zodNumber.max(numberSchema.maximum)
      }
      if (numberSchema.multipleOf !== undefined) {
        zodNumber = zodNumber.multipleOf(numberSchema.multipleOf)
      }

      return applyNullable(zodNumber, schema)
    }
    case 'boolean': {
      return applyNullable(z.boolean(), schema)
    }
    case 'null': {
      return z.null()
    }
    case 'array': {
      const arraySchema = schema as JsonSchemaArray

      let itemZod: z.ZodTypeAny = z.unknown()
      if (arraySchema.items) {
        if (Array.isArray(arraySchema.items)) {
          // Tuple-style arrays (items is an array of schemas)
          const itemSchemas = arraySchema.items.map(convertJsonSchemaToZod)
          if (itemSchemas.length === 1) {
            itemZod = itemSchemas[0]
          } else {
            // Create a union of the item types
            itemZod = z.union(itemSchemas as [z.ZodTypeAny, z.ZodTypeAny, ...z.ZodTypeAny[]])
          }
        } else {
          // Single schema for all items
          itemZod = convertJsonSchemaToZod(arraySchema.items)
        }
      }

      let zodArray = z.array(itemZod)

      // Apply array constraints
      if (arraySchema.minItems !== undefined) {
        zodArray = zodArray.min(arraySchema.minItems)
      }
      if (arraySchema.maxItems !== undefined) {
        zodArray = zodArray.max(arraySchema.maxItems)
      }

      return applyNullable(zodArray, schema)
    }
    case 'object': {
      const objectSchema = schema as JsonSchemaObject
      const properties = objectSchema.properties ?? {}
      const required = new Set(objectSchema.required ?? [])

      const shapeEntries = Object.entries(properties).map(([key, child]) => {
        const childZod = convertJsonSchemaToZod(child)
        const zodNode = required.has(key) ? childZod : childZod.optional()
        return [key, zodNode] as const
      })

      const shape: Record<string, z.ZodTypeAny> = Object.fromEntries(shapeEntries)
      let zodObject = z.object(shape)

      // Handle additionalProperties
      if (objectSchema.additionalProperties === false) {
        zodObject = (zodObject as any).strict()
      }
      // Note: additionalProperties: true or schema not fully supported in Zod

      return applyNullable(zodObject, schema)
    }
    default: {
      return z.unknown()
    }
  }
}

export const jsonSchemaStringToZod = (schemaString: string): z.ZodTypeAny | null => {
  try {
    const parsed = JSON.parse(schemaString) as unknown
    if (!isJsonSchema(parsed)) {
      return null
    }
    return convertJsonSchemaToZod(parsed)
  } catch {
    return null
  }
}
